// v3.15-1216 MM-002 — Financial Lifecycle Tracking (read-only projection).
//
// Displays the replayable projection of every architecture-driven earning
// (compensation / bonus / spiff / chargeback / payroll) across four stages:
//   Pending → Added to Money Manager → Approved → Paid.
//
// This page NEVER writes to compensation / bonus / spiff / chargeback /
// payroll records — it only writes to `compensation_lifecycle_events`
// (append-only) via the /transition and /transition/bulk endpoints.
//
// All amounts, employees, plans, and stages are derived server-side.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Loader2, RefreshCw, Filter, CheckCircle2, Banknote, Clock, Link2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const STAGES = [
  { key: 'pending',                label: 'Pending',                icon: Clock,        color: 'amber' },
  { key: 'added_to_money_manager', label: 'Added to Money Manager', icon: Link2,        color: 'sky' },
  { key: 'approved',               label: 'Approved',               icon: CheckCircle2, color: 'indigo' },
  { key: 'paid',                   label: 'Paid',                   icon: Banknote,     color: 'emerald' },
];
const SOURCE_TYPES = ['all', 'compensation', 'bonus', 'spiff', 'chargeback', 'payroll'];

const fmtMoney = (n) => (n === null || n === undefined || n === '')
  ? '—'
  : `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return s; }
};
const safeErrText = (err, fb) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return d.message || d.code || fb;
  return err?.message || fb;
};

// v3.15-1216 — small color palette that Tailwind can statically resolve.
const stageColors = {
  amber:   { pill: 'bg-amber-100 text-amber-800',    ring: 'ring-amber-300',   dot: 'bg-amber-500'   },
  sky:     { pill: 'bg-sky-100 text-sky-800',        ring: 'ring-sky-300',     dot: 'bg-sky-500'     },
  indigo:  { pill: 'bg-indigo-100 text-indigo-800',  ring: 'ring-indigo-300',  dot: 'bg-indigo-500'  },
  emerald: { pill: 'bg-emerald-100 text-emerald-800',ring: 'ring-emerald-300', dot: 'bg-emerald-500' },
};

const StageBadge = ({ stage }) => {
  const cfg = STAGES.find((s) => s.key === stage) || STAGES[0];
  const c = stageColors[cfg.color];
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full ${c.pill}`}
      data-testid={`fl-stage-badge-${stage}`}
    >
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
};

export default function FinancialLifecyclePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [rows, setRows]           = useState([]);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stage, setStage]         = useState('all');
  const [sourceType, setSourceType] = useState('all');
  const [selected, setSelected]   = useState(() => new Set());
  const [busy, setBusy]           = useState(false);

  // Ptc-admin-only guard.
  useEffect(() => {
    if (user && user.role !== 'ptc_admin') {
      toast.error('Access denied');
      navigate('/');
    }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = {};
      if (stage !== 'all')       params.stage = stage;
      if (sourceType !== 'all')  params.source_type = sourceType;
      const [listRes, sumRes] = await Promise.all([
        api.get('/financial-lifecycle', { params }),
        api.get('/financial-lifecycle/summary', {
          params: sourceType !== 'all' ? { source_type: sourceType } : {},
        }),
      ]);
      setRows(listRes.data?.items || []);
      setSummary(sumRes.data);
      setSelected(new Set());
    } catch (e) {
      toast.error(safeErrText(e, 'Failed to load lifecycle'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [stage, sourceType]);

  useEffect(() => { load(); }, [load]);

  const filteredCount = rows.length;
  const filteredTotal = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.amount || 0), 0),
    [rows],
  );

  const toggleRow = (ref) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(ref)) n.delete(ref); else n.add(ref);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map(r => r.source_ref_id))));
  };

  const bulkTransition = async (targetStage) => {
    if (selected.size === 0) { toast.error('Select at least one row'); return; }
    // Group selected refs by source_type — the API is per-type.
    const grouped = {};
    for (const r of rows) {
      if (selected.has(r.source_ref_id)) {
        (grouped[r.source_type] ||= []).push(r.source_ref_id);
      }
    }
    setBusy(true);
    try {
      let totalAdded = 0, totalSkipped = 0;
      for (const [st, refs] of Object.entries(grouped)) {
        const { data } = await api.post('/financial-lifecycle/transition/bulk', {
          source_type: st, stage: targetStage, source_ref_ids: refs,
        });
        totalAdded += data?.counts?.added || 0;
        totalSkipped += data?.counts?.skipped || 0;
      }
      toast.success(`Marked ${targetStage}: added ${totalAdded} · skipped ${totalSkipped}`);
      await load();
    } catch (e) {
      toast.error(safeErrText(e, `Failed to mark ${targetStage}`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="financial-lifecycle-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <button
            type="button"
            onClick={() => navigate('/money-manager')}
            className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-2"
            data-testid="fl-back-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Money Manager
          </button>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Filter className="h-5 w-5 text-indigo-600" /> Financial Lifecycle
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Replayable projection over immutable compensation records + Money Manager consumption.
            No source-of-truth records are modified here.
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          size="sm"
          disabled={refreshing}
          data-testid="fl-refresh-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary cards — one per stage */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {STAGES.map((s) => {
          const c = stageColors[s.color];
          const bucket = summary?.stages?.find((x) => x.stage === s.key);
          const isActive = stage === s.key;
          const Icon = s.icon;
          return (
            <button
              type="button"
              key={s.key}
              onClick={() => setStage(isActive ? 'all' : s.key)}
              className={`text-left bg-white border rounded-lg p-3 transition ${isActive ? `ring-2 ${c.ring} border-transparent` : 'hover:border-slate-300'}`}
              data-testid={`fl-summary-card-${s.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full ${c.pill}`}>
                  <Icon className="h-3 w-3 inline mr-1" /> {s.label}
                </span>
                <span className={`h-2 w-2 rounded-full ${c.dot}`} />
              </div>
              <div className="text-2xl font-semibold text-slate-900" data-testid={`fl-summary-count-${s.key}`}>
                {bucket ? bucket.count : 0}
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-testid={`fl-summary-total-${s.key}`}>
                {fmtMoney(bucket?.total_amount)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="bg-white border rounded-lg p-3 mb-3 flex flex-wrap items-center gap-3 text-xs">
        <div className="flex items-center gap-1">
          <span className="text-slate-500 mr-1">Stage:</span>
          <button
            type="button"
            onClick={() => setStage('all')}
            className={`px-2.5 py-1 rounded-full border ${stage === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
            data-testid="fl-stage-filter-all"
          >All</button>
          {STAGES.map((s) => (
            <button
              type="button"
              key={s.key}
              onClick={() => setStage(s.key)}
              className={`px-2.5 py-1 rounded-full border ${stage === s.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
              data-testid={`fl-stage-filter-${s.key}`}
            >{s.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-1 ml-4">
          <span className="text-slate-500 mr-1">Source:</span>
          {SOURCE_TYPES.map((t) => (
            <button
              type="button"
              key={t}
              onClick={() => setSourceType(t)}
              className={`px-2.5 py-1 rounded-full border capitalize ${sourceType === t ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
              data-testid={`fl-source-filter-${t}`}
            >{t}</button>
          ))}
        </div>
        <div className="ml-auto text-slate-600" data-testid="fl-filter-summary">
          <span className="font-medium">{filteredCount}</span> record{filteredCount === 1 ? '' : 's'}
          <span className="text-slate-400 mx-2">·</span>
          total <span className="font-medium">{fmtMoney(filteredTotal)}</span>
        </div>
      </div>

      {/* Bulk-action bar (only when rows selected) */}
      {selected.size > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 mb-3 flex items-center justify-between text-xs" data-testid="fl-bulk-bar">
          <div className="text-indigo-800">
            <span className="font-semibold">{selected.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => bulkTransition('approved')}
              disabled={busy}
              data-testid="fl-bulk-approve-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark Approved</>}
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => bulkTransition('paid')}
              disabled={busy}
              data-testid="fl-bulk-pay-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Banknote className="h-3.5 w-3.5 mr-1" /> Mark Paid</>}
            </Button>
          </div>
        </div>
      )}

      {/* Projection table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="py-12 flex items-center justify-center text-slate-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading lifecycle projection…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm italic" data-testid="fl-empty">
            No records match the current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="fl-table">
              <thead>
                <tr className="bg-slate-50 border-b sticky top-0">
                  <th className="px-2 py-2 w-8 text-center">
                    <input
                      type="checkbox"
                      checked={selected.size === rows.length && rows.length > 0}
                      onChange={toggleAll}
                      data-testid="fl-toggle-all"
                    />
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Employee</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Company</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Role</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Source</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Plan</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">Amount</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Stage</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Pending</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Added</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Approved</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Paid</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Ref</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isSel = selected.has(r.source_ref_id);
                  return (
                    <tr
                      key={`${r.source_type}:${r.source_ref_id}`}
                      className={`border-b last:border-0 ${isSel ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
                      data-testid={`fl-row-${r.source_ref_id}`}
                    >
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleRow(r.source_ref_id)}
                          data-testid={`fl-checkbox-${r.source_ref_id}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-slate-800 font-medium">{r.employee_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.company_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-600 capitalize">{r.employee_role || '—'}</td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded capitalize">
                          {r.source_type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {r.compensation_plan || '—'}
                        {r.compensation_plan_version ? (
                          <span className="text-[9px] text-slate-400 ml-1">v{r.compensation_plan_version}</span>
                        ) : null}
                      </td>
                      <td className={`px-3 py-2 text-right font-semibold ${Number(r.amount) < 0 ? 'text-rose-700' : 'text-slate-900'}`}>
                        {fmtMoney(r.amount)}
                      </td>
                      <td className="px-3 py-2"><StageBadge stage={r.current_stage} /></td>
                      <td className="px-3 py-2 text-slate-500">{fmtDate(r.stage_entry_dates?.pending)}</td>
                      <td className="px-3 py-2 text-slate-500">
                        {r.mm_entry_id ? (
                          <div>
                            <div>{fmtDate(r.stage_entry_dates?.added_to_money_manager)}</div>
                            {r.mm_client_name && (
                              <div className="text-[9px] text-slate-400 italic">{r.mm_client_name}</div>
                            )}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{fmtDate(r.stage_entry_dates?.approved)}</td>
                      <td className="px-3 py-2 text-slate-500">{fmtDate(r.stage_entry_dates?.paid)}</td>
                      <td className="px-3 py-2 text-[10px] text-slate-400 font-mono">{(r.source_ref_id || '').slice(0, 8)}…</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-3 text-[10px] text-slate-400 italic" data-testid="fl-footer-note">
        Lifecycle events are append-only. Approved / Paid transitions are recorded in
        <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded mx-1">compensation_lifecycle_events</code>
        and never mutate the underlying compensation, bonus, spiff, chargeback, or payroll records.
      </div>
    </div>
  );
}
